// Browser file handling for the documents feature. Same exports as documentFiles.ts.
import { PickedDocumentFile } from '../../types';

const doc = () => (globalThis as any).document;
const win = () => (globalThis as any).window;

/** A hidden <input type="file">. Resolves null when the parent cancels the dialog. */
export function pickDocumentFile(extensions: string[]): Promise<PickedDocumentFile | null> {
  return new Promise(resolve => {
    const d = doc();
    if (!d?.createElement) {
      resolve(null);
      return;
    }
    const input = d.createElement('input');
    input.type = 'file';
    if (extensions.length > 0) input.accept = extensions.map(ext => `.${ext}`).join(',');
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      resolve(
        file
          ? { name: file.name, size: file.size, type: file.type || 'application/octet-stream', file }
          : null,
      );
    };
    // Fired by current browsers when the dialog is dismissed.
    input.addEventListener?.('cancel', () => resolve(null));
    input.click();
  });
}

export async function saveBlob(blob: Blob, fileName: string, _contentType: string): Promise<string> {
  const url = URL.createObjectURL(blob);
  const link = doc().createElement('a');
  link.href = url;
  link.download = fileName;
  doc().body.appendChild(link);
  link.click();
  doc().body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'Download started';
}

/** Opens the browser's own viewer (PDF, image) in a new tab. */
export async function openBlob(blob: Blob, fileName: string, contentType: string): Promise<string> {
  const typed = blob.type ? blob : new Blob([blob], { type: contentType } as any);
  const url = URL.createObjectURL(typed);
  const opened = win()?.open(url, '_blank');
  if (!opened) {
    // Pop-up blocked: fall back to a download so the parent still gets the file.
    URL.revokeObjectURL(url);
    return saveBlob(blob, fileName, contentType);
  }
  // The new tab holds its own reference once loaded.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return 'Opened';
}

export async function readBlobText(blob: Blob): Promise<string> {
  try {
    return await (blob as any).text();
  } catch {
    return '';
  }
}
